import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  useQuery,
  useMutation,
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from "react-query";
import axios from "axios";
import { globalHistory, Location, navigate } from "@reach/router";
import { useState } from "react";
import decryptCode from "../utils/decryptCode";
import useUpload from "./useUpload";
import {
  API_BACKEND_URL,
  API_PROXY,
  API_SONICOLA_URL,
} from "../services/apiUrls";
import { resolveRedirectedRadioid } from "../config/radioRedirects";
import {
  createEventAttemptId,
  EVENT_TYPES,
  flushQueuedEvents,
  initializeEventEmitter,
  rememberInteractiveLogin,
  reportEvent,
  reportRedeemSuccess,
} from "../services/eventEmitter";
import {
  emitMarketingEvent,
  MARKETING_EVENTS as MARKETING_UI_EVENTS,
} from "../features/marketing/eventBus";
import { normalizeMarketingConfig } from "../features/marketing/normalizer";
import {
  acquireRedeemSubmission,
  getStoredRedeemCodes,
  markRedeemSubmissionSuccess,
  removeStoredRedeemCode,
  releaseRedeemSubmission,
} from "../services/eventEmitter/storage";
import {
  getEntryIntent,
  restartRadioEntry,
  clearLegacyRedirectLogin,
  getLegacyRedirectLogin,
  getLoginResumePath,
} from "../features/entryFlow/entryIntentStorage";
import {
  classifyEmailLoginError,
  describeVerificationCodeInput,
  getAxiosErrorDetails,
  normalizeEmailForLogin,
  normalizeNotificationId,
  normalizeVerificationCode,
  recordEmailLoginDiagnostic,
} from "../services/emailLoginDiagnostics";
import {
  getTermsExtraValue,
  isTermsAcceptedValue,
} from "../helpers/termsAcceptance";

const RadioContext = createContext({
  nameseo: null,
  isTask: false,
  currentRadioid: () => { },
  setCurrentRadioid: () => { },
});
const UserContext = createContext(null);

export const apiQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30000,
    },
  },
});

export { API_PROXY, API_SONICOLA_URL, API_BACKEND_URL };

function fetchPost(method, dataToSend) {
  // console.log("fetchPost", method, dataToSend);
  return async () => {
    const { data } = await axios.post(API_SONICOLA_URL + method, dataToSend);
    // console.log("fetchPost data", data);
    if (typeof data?.data !== "undefined") {
      return data.data;
    }
    return data;
  };
}

function fetchPostPage(method, initialParams) {
  return async (params) => {
    const sendParams =
      params.pageParam !== undefined ? params.pageParam : initialParams;
    // console.log("fetchPostPage", method, initialParams);
    const { data } = await axios.post(API_SONICOLA_URL + method, sendParams);
    return data;
  };
}

const isBusinessSuccess = (result) => {
  if (!result) return false;
  if (typeof result?.status === "string") {
    return result.status === "success";
  }
  return true;
};

const getLoginMethod = (type) => {
  switch (type) {
    case "facebook":
    case "google":
    case "apple":
      return type;
    default:
      return "normal";
  }
};

const getEventAttemptId = (variables, context) => {
  return context?.eventAttemptId ?? variables?.eventAttemptId ?? null;
};

const SIGNUP_RECENCY_THRESHOLD_MS = 60 * 60 * 1000;
const EMAIL_LOGIN_CHECK_TIMEOUT_MS = 10 * 1000;

const isEmailLoginPendingResponse = (response) => {
  if (response?.status !== "fail") return false;

  const message = String(response?.msg ?? response?.message ?? "").toLowerCase();
  return message.includes("login not found");
};

const toEmailLoginCheckFailure = ({ response, notificationid }) => ({
  message: response?.msg ?? response?.message ?? "Login not found",
  status: response?.status ?? "fail",
  notificationid,
  occurredAt: new Date().toISOString(),
});

const parseSignupTimestampMs = (signup) => {
  if (signup === undefined || signup === null || signup === "") {
    return null;
  }

  const signupNumber = Number(signup);
  if (Number.isNaN(signupNumber) || signupNumber <= 0) {
    return null;
  }

  if (signupNumber > 1e12) {
    return signupNumber;
  }

  return signupNumber * 1000;
};

const shouldEmitRegisterEvent = (user) => {
  const signupAtMs = parseSignupTimestampMs(user?.signup);
  if (!signupAtMs) return false;

  return Math.abs(Date.now() - signupAtMs) <= SIGNUP_RECENCY_THRESHOLD_MS;
};

const buildRegisterPayload = (result, method, providerUserId = null) => {
  const signupAtMs = parseSignupTimestampMs(result?.signup);

  return {
    method,
    providerUserId,
    signup: result?.signup ?? null,
    signupAt: signupAtMs ? new Date(signupAtMs).toISOString() : null,
  };
};

const normalizeAcceptTermsInput = (value) => {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.prototype.hasOwnProperty.call(value, "extra")
  ) {
    return {
      extra: value.extra,
      formMetadata: Array.isArray(value.formMetadata) ? value.formMetadata : null,
      isCustomForm: !!value.isCustomForm,
    };
  }

  return {
    extra: value,
    formMetadata: null,
    isCustomForm: false,
  };
};

const fetchUserExtra = async ({ radioid, token }) => {
  const response = await axios.get(
    `${API_PROXY}getUserExtra?${new URLSearchParams({ radioid, token }).toString()}`
  );

  if (response.status !== 200) {
    throw new Error("Could not retrieve user terms acceptance");
  }

  const data = response.data;
  if (typeof data?.extra === "string") {
    try {
      data.extra = JSON.parse(data.extra.replaceAll("'", '"'));
    } catch (_error) {
      // Legacy values such as "terms" are intentionally left as strings.
    }
  }

  return data;
};

const addUserExtra = async ({ radioid, token, extra }) => {
  const response = await axios.get(
    `${API_PROXY}addUserExtra?${new URLSearchParams({
      radioid,
      token,
      extra,
    }).toString()}`
  );

  if (response.status !== 200) {
    throw new Error("Could not persist user terms acceptance");
  }

  return response.data;
};

export const useLocals = (lat, lng, search) => {
  /**
   * @type DataToSend
   * @property {number} limit
   * @property {number} offset
   * @property {number} latitude
   * @property {number} longitude
   * @property {string*} search
   */
  const dataToSend = {
    limit: 100,
    offset: 0,
    latitude: -34.90198799981324,
    longitude: -56.13432873602192,
    search: "",
  };
  if (lat !== undefined && lng !== undefined && lat !== null && lng !== null) {
    dataToSend.latitude = lat; //-34.90198799981324
    dataToSend.longitude = lng; //-56.13432873602192,
  }
  if (search !== "" && search !== undefined) {
    dataToSend.search = search;
  }
  const query = useQuery(
    ["locals", lat, lng, search],
    fetchPost(`locals`, dataToSend),
    {
      enabled:
        (search !== "" && search !== undefined) ||
        (lat !== null &&
          lng !== null &&
          lat !== undefined &&
          lng !== undefined),
    }
  );
  return query;
};

export const useRadioidByNameseo = (nameseo) => {
  const getRadioQuery = useQuery(
    [`getradionameseo`, nameseo],
    async () => {
      const params = { nameseo: nameseo };
      try {
        const data = await new Promise((onResolve, onReject) =>
          axios
            .post(`${API_BACKEND_URL}getradionameseo`, params, { timeout: 10000 })
            .then((response) => {
              if (Object.hasOwnProperty.call(response, "status")) {
                if (response.status === 200) {
                  if (typeof response.data !== "undefined") {
                    if (typeof response.data.data !== "undefined") {
                      // console.log(
                      //   "getradionameseo response.data.data",
                      //   response.data.data
                      // );
                      onResolve(response.data.data);
                    } else {
                      // console.log(
                      //   "getradionameseo response.data",
                      //   response.data
                      // );
                      onResolve(response.data);
                    }
                  } else {
                    // console.log("getradionameseo response", response);
                    onResolve(response);
                  }
                }
              }
              onReject();
            })
            .catch((error) => {
              console.log("Handle error", error);
              onReject(error);
            })
        );
        return data;
      } catch (e) {
        throw e;
      }
    },
    {
      enabled:
        !!nameseo &&
        nameseo !== undefined &&
        nameseo !== null &&
        typeof nameseo === "string",
      refetchInterval: 10 * 1000,
    }
  );

  const marketingConfig = useMemo(
    () => normalizeMarketingConfig(getRadioQuery.data?.marketing),
    [getRadioQuery.data?.marketing]
  );

  return {
    ...getRadioQuery,
    radioid: getRadioQuery.data?.radioid ? getRadioQuery.data?.radioid : null,
    name: getRadioQuery.data?.name ?? null,
    marketingConfig,
  };
};

export const useLocal = (radioid) => {
  const { currentRadioid, setCurrentRadioid } = useContext(RadioContext);

  useEffect(() => {
    if (radioid !== undefined && currentRadioid !== radioid) {
      setCurrentRadioid(parseInt(radioid));
    }
  }, [radioid, currentRadioid, setCurrentRadioid]);

  const query = useQuery(
    [`singlecola`, parseInt(currentRadioid)],
    async () => {
      try {
        const data = await new Promise((onResolve, onReject) =>
          axios
            .post(`${API_SONICOLA_URL}singlecola`, {
              radioid: currentRadioid,
            })
            .then((response) => {
              if (Object.hasOwnProperty.call(response, "status")) {
                if (response.status === 200) {
                  if (typeof response.data.data !== "undefined") {
                    // console.log(
                    //   "singlecola response.data.data",
                    //   response.data.data
                    // );
                    onResolve(response.data.data);
                  } else {
                    // console.log("singlecola response.data", response.data);
                    onResolve(response.data);
                  }
                }
              }
              onReject();
            })
            .catch((error) => {
              console.log("Handle error", error);
              onReject(error);
            })
        );
        return data;
      } catch (e) {
        throw e;
      }
    },
    {
      enabled:
        !isNaN(currentRadioid) &&
        currentRadioid !== null &&
        currentRadioid !== undefined,
      refetchInterval: 10 * 1000,
    }
  );

  return {
    ...query,
    radioid: currentRadioid,
    nameseo: query.data?.nameseo ? query.data?.nameseo : null,
  };
};

export const useTerms = (nameseo) => {
  const { user } = useUser();
  const [form, setForm] = useState(null);
  const synchronizationKeyRef = useRef(null);
  const { radioid, data: radioData } = useRadioidByNameseo(nameseo);

  const extraQuery = useQuery(
    ["extra", radioid, user?.userid],
    () => fetchUserExtra({ radioid, token: user?.token }),
    {
      enabled: !!radioid && !!user?.token,
    }
  );

  const termsQuery = useQuery(
    ["terms", nameseo, user?.userid],
    async () => {
      try {
        const data = await new Promise((onResolve, onReject) =>
          axios
            .get(`${API_PROXY}getTermsForm/${nameseo}`)
            .then((response) => {
              if (Object.hasOwnProperty.call(response, "status")) {
                if (response.status === 200) {
                  onResolve(response.data);
                }
              }
              onReject();
            })
            .catch((error) => {
              console.log("Handle error", error);
              onReject(error);
            })
        );
        return data;
      } catch (e) {
        throw e;
      }
    },
    {
      enabled: !!nameseo,
    }
  );

  const termsScope = termsQuery.data?.termsScope ?? null;
  const referenceRadioid = termsScope?.referenceRadioid ?? null;
  const hasSeparateReference =
    !!referenceRadioid && String(referenceRadioid) !== String(radioid);

  const referenceExtraQuery = useQuery(
    ["extra", referenceRadioid, user?.userid],
    () => fetchUserExtra({ radioid: referenceRadioid, token: user?.token }),
    {
      enabled: hasSeparateReference && !!user?.token,
    }
  );

  const formHasCustomFields = !!form;
  const currentTermsAccepted = useMemo(
    () =>
      isTermsAcceptedValue(getTermsExtraValue(extraQuery.data), {
        hasCustomForm: formHasCustomFields,
      }),
    [extraQuery.data, formHasCustomFields]
  );
  const referenceTermsAccepted = useMemo(
    () =>
      hasSeparateReference &&
      isTermsAcceptedValue(getTermsExtraValue(referenceExtraQuery.data), {
        hasCustomForm: formHasCustomFields,
      }),
    [hasSeparateReference, referenceExtraQuery.data, formHasCustomFields]
  );
  const termsAccepted = currentTermsAccepted || referenceTermsAccepted;

  const acceptTerms = useMutation(async (variables) => {
    if (user?.userid === undefined || user?.userid === null) {
      throw new Error("user not logged");
    }
    if (radioid === undefined || radioid === null) {
      throw new Error("Radio not selected");
    }

    const normalizedInput = normalizeAcceptTermsInput(variables);
    const targetRadioids = Array.from(
      new Set([radioid, referenceRadioid].filter(Boolean).map(String))
    );

    return Promise.all(
      targetRadioids.map((targetRadioid) =>
        addUserExtra({
          radioid: targetRadioid,
          token: user?.token,
          extra: normalizedInput.extra,
        })
      )
    );
  });

  const synchronizeTerms = useMutation(async (targetRadioids) =>
    Promise.all(
      targetRadioids.map((targetRadioid) =>
        addUserExtra({
          radioid: targetRadioid,
          token: user?.token,
          extra: ["terms"],
        })
      )
    )
  );

  const missingAcceptanceRadioids = useMemo(() => {
    if (!termsAccepted) return [];

    const missing = [];
    if (!currentTermsAccepted && radioid) missing.push(String(radioid));
    if (hasSeparateReference && !referenceTermsAccepted) {
      missing.push(String(referenceRadioid));
    }
    return missing;
  }, [
    currentTermsAccepted,
    hasSeparateReference,
    radioid,
    referenceRadioid,
    referenceTermsAccepted,
    termsAccepted,
  ]);

  useEffect(() => {
    const synchronizationKey = [
      user?.userid,
      termsScope?.key,
      missingAcceptanceRadioids.join(","),
    ].join(":");

    if (
      !termsAccepted ||
      !extraQuery.isSuccess ||
      (hasSeparateReference && !referenceExtraQuery.isSuccess) ||
      missingAcceptanceRadioids.length === 0 ||
      synchronizeTerms.isLoading ||
      synchronizationKeyRef.current === synchronizationKey
    ) {
      if (missingAcceptanceRadioids.length === 0) {
        synchronizationKeyRef.current = null;
      }
      return;
    }

    synchronizationKeyRef.current = synchronizationKey;
    synchronizeTerms.mutate(missingAcceptanceRadioids, {
      onSuccess: () => {
        extraQuery.refetch();
        if (hasSeparateReference) referenceExtraQuery.refetch();
      },
    });
  }, [
    extraQuery,
    hasSeparateReference,
    missingAcceptanceRadioids,
    referenceExtraQuery,
    synchronizeTerms,
    termsAccepted,
    termsScope?.key,
    user?.userid,
  ]);

  useEffect(() => {
    if (termsQuery?.data?.text) {
      const dom = document.createElement("div");
      dom.innerHTML = termsQuery.data.text;
      const _form = dom.querySelector("form");
      console.log("has form?", _form);
      if (_form) {
        const formGroups = _form.querySelectorAll(".form-group");
        const inputs = [];
        formGroups.forEach((formGroup) => {
          const label = formGroup.querySelector("label");
          const input = formGroup.querySelector("input, select, textarea");
          const small = formGroup.querySelector("small");
          const link = label ? label.querySelector("a") : null;
          inputs.push({
            label,
            input,
            small,
            link,
          });
        });
        setForm(inputs);
      } else {
        setForm(null);
      }
    }
  }, [termsQuery?.data]);

  const links = useMemo(() => {
    if (form) {
      const links = [];
      form.forEach(({ link }) => {
        if (link) {
          links.push(link);
        }
      });
      return links;
    }
    return null;
  }, [form]);

  return {
    ...termsQuery,
    acceptTerms: acceptTerms,
    radioid: radioid,
    radioData: radioData,
    form: form,
    links: links,
    extraQuery: extraQuery,
    referenceExtraQuery: referenceExtraQuery,
    termsScope: termsScope,
    termsAccepted: termsAccepted,
    isTermsAcceptanceLoading:
      extraQuery.isLoading ||
      (hasSeparateReference && referenceExtraQuery.isLoading),
  };
};

export const useRadioByNameseo = () => {
  const { nameseo } = useContext(RadioContext);

  // useEffect(() => {
  //   console.log("useTask useEffect change radioid", radioid);
  // }, [radioid]);

  const getRadioQuery = useQuery(
    [`getradionameseo`, nameseo],
    async () => {
      try {
        const params = { nameseo: nameseo };
        const data = await new Promise((onResolve, onReject) =>
          axios
            .post(`${API_BACKEND_URL}getradionameseo`, params)
            .then((response) => {
              if (Object.hasOwnProperty.call(response, "status")) {
                if (response.status === 200) {
                  if (typeof response.data?.data !== "undefined") {
                    onResolve(response.data.data);
                  } else {
                    onResolve(response.data);
                  }
                }
              }
              onReject();
            })
            .catch((error) => {
              console.log("Handle error", error);
              onReject(error);
            })
        );
        return data;
      } catch (e) {
        throw e;
      }
    },
    {
      enabled:
        nameseo !== undefined &&
        nameseo !== null &&
        typeof nameseo === "string",
      refetchInterval: 10 * 1000,
      retry: 3,
      retryDelay: 500,
    }
  );

  return {
    getRadioQuery: getRadioQuery,
    radioid: getRadioQuery?.data?.radioid,
    nameseo: nameseo,
  };
};

export const useNextSong = (radioid) => {
  const { data: localData } = useLocal(radioid);

  const actualRadioid = useMemo(() => {
    return localData ? localData.radioid : radioid;
  }, [localData, radioid]);

  const query = useQuery(
    [`nextsong`, actualRadioid],
    fetchPost(`nextsong`, {
      // token: token,
      radioid: actualRadioid,
      limit: 100,
      offset: 0,
    }),
    {
      enabled: actualRadioid !== null && typeof actualRadioid !== "undefined",
      refetchInterval: 10000,
    }
  );

  const sortedNextSongs = useMemo(() => {
    if (Array.isArray(query?.data)) {
      return query?.data.sort((a, b) => {
        // sort songs by the date they were added, ascending
        const aDate = new Date(parseInt(a.request));
        const bDate = new Date(parseInt(b.request));
        return aDate - bDate;
      }).sort((a, b) => {
        // Sort songs by the number of votes, descending
        const aVotes = parseInt(a.votes, 10) || 0;
        const bVotes = parseInt(b.votes, 10) || 0;
        return bVotes - aVotes;
      });
    }
    return query?.data;
  }, [query?.data]);

  const filteredData = useMemo(() => {
    if (!sortedNextSongs) return sortedNextSongs;

    const now = Math.floor(Date.now() / 1000);
    const twentyFourHoursAgo = now - 86400;

    return sortedNextSongs.filter(song => {
      const requestTime = parseInt(song.request, 10);
      return requestTime >= twentyFourHoursAgo;
    });
  }, [sortedNextSongs]);

  return {
    ...query,
    data: filteredData,
  };
};

export const useUser = () => {
  const {
    token,
    user,
    setUser,
    login,
    logout,
    userInitiated,
    loginRedirect,
    setLoginRedirect,
  } = useContext(UserContext);

  const userQuery = useQuery(
    [`user`, token],
    async () => {
      const qF = fetchPost("user", { token: token });
      const data = await qF();
      setUser({ ...data, token: token });
      return data;
    },
    { enabled: token !== null }
  );

  const setRadioVisited = (radioinfo) => {
    if (!radioinfo) return;
    const existingRadio = JSON.parse(window.localStorage.getItem('radio')) ?? {};
    const updatedRadio = { ...existingRadio, ...radioinfo };
    window.localStorage.setItem('radio', JSON.stringify(updatedRadio));
  };

  const getRadioInfo = () => {
    return JSON.parse(window.localStorage.getItem('radio') ?? null);
  }

  // Define the "login" mutation
  apiQueryClient.setMutationDefaults("login", {
    mutationFn: async (dataToSend) => {
      let url = `login`;
      if (dataToSend?.type === "facebook" || dataToSend?.type === "google" || dataToSend?.type === 'apple') {
        url = `addUser`;
      }
      // console.log("login", url, dataToSend);
      const qF = fetchPost(url, dataToSend);
      return await qF();
    },
    onMutate: async (variables) => ({
      eventAttemptId: variables?.eventAttemptId ?? createEventAttemptId(),
    }),
    onSuccess: (result, variables, context) => {
      if (result !== null && result.hasOwnProperty("token")) {
        // Replace optimistic todo in the user list with the result
        apiQueryClient.setQueryData("login", (old) => result);
        rememberInteractiveLogin(result);
        const loginMethod = getLoginMethod(variables?.type);
        const providerUserId = variables?.oauthid ?? null;
        void reportEvent({
          type: EVENT_TYPES.LOGIN,
          user: {
            ...result,
            email: result?.email ?? variables?.email ?? null,
          },
          payload: {
            method: loginMethod,
            providerUserId,
          },
          resolutionMode: "login",
          attemptId: getEventAttemptId(variables, context),
        });
        if (shouldEmitRegisterEvent(result)) {
          void reportEvent({
            type: EVENT_TYPES.REGISTER,
            user: {
              ...result,
              email: result?.email ?? variables?.email ?? null,
            },
            payload: buildRegisterPayload(result, loginMethod, providerUserId),
            resolutionMode: "login",
          });
        }
        login(result.token, result);
      }
      return result;
    },
    onError: async (error, variables, context) => {
      // console.log("on login error");
      if (variables?.type === 'apple') {
        try {
          const appleParams = {
            code: variables.oauthid,
            id_token: variables.oauth,
            state: variables.state ?? 'state'
          };
          const qF = fetchPost('loginapple', appleParams);
          const result = await qF();

          if (result !== null && result.hasOwnProperty("token")) {
            apiQueryClient.setQueryData("login", (old) => result);
            rememberInteractiveLogin(result);
            const providerUserId = variables?.oauthid ?? null;
            void reportEvent({
              type: EVENT_TYPES.LOGIN,
              user: {
                ...result,
                email: result?.email ?? variables?.email ?? null,
              },
              payload: {
                method: "apple",
                providerUserId: variables?.oauthid ?? null,
                fallback: true,
              },
              resolutionMode: "login",
              attemptId: getEventAttemptId(variables, context),
            });
            if (shouldEmitRegisterEvent(result)) {
              void reportEvent({
                type: EVENT_TYPES.REGISTER,
                user: {
                  ...result,
                  email: result?.email ?? variables?.email ?? null,
                },
                payload: buildRegisterPayload(result, "apple", providerUserId),
                resolutionMode: "login",
              });
            }
            login(result.token, result);
            return result;
          }
        } catch (appleError) {
          console.log("Apple fallback login also failed", appleError);
        }
      }
      // Remove optimistic user from the user list
      apiQueryClient.setQueryData("login", (old) => ({
        token: null,
        name: "",
      }));
    },
    retry: 1,
  });

  const loginMutation = useMutation("login");
  const loginData = loginMutation.data;

  const editMutation = useMutation({
    mutationFn: async (dataToSend) => {
      const { data } = await axios.post(
        API_SONICOLA_URL + "editUser",
        dataToSend
      );
      return data;
    },
    onSuccess: (result, variables, context) => {
      console.log("on edit success", result);
    },
    onError: (error, variables, context) => {
      console.log("on edit error", error);
    },
    onSettled: (data, error, variables, context) => {
      console.log("on edit settled", data, error);
      apiQueryClient.invalidateQueries("user");
    },
  });

  const updateUser = () => {
    apiQueryClient.invalidateQueries("user");
  };

  const { upload } = useUpload();

  const uploadAvatarMutation = useMutation({
    mutationFn: async (dataToSend) => {
      const { data } = await upload(
        API_SONICOLA_URL + "uploadImage",
        dataToSend
      );
      return data;
    },
    onSuccess: (result, variables, context) => {
      console.log("on upload avatar success", result);
      if (typeof result.status === "string" && result.status === "success") {
        editMutation.mutate({ ...user, avatar: result.url });
      }
    },
    onError: (error, variables, context) => {
      console.log("on upload avatar error", error);
    },
    onSettled: (data, error, variables, context) => {
      console.log("on upload avatar settled", data, error);
    },
  });

  const [notificationid, setNotificationid] = useState(null);
  const [emailLoginCheckNotificationId, setEmailLoginCheckNotificationId] =
    useState(null);
  const [emailCodeConfirmedAt, setEmailCodeConfirmedAt] = useState(null);
  const [emailLoginCheckFailure, setEmailLoginCheckFailure] = useState(null);

  useEffect(() => {
    setEmailLoginCheckNotificationId(null);
    setEmailCodeConfirmedAt(null);
    setEmailLoginCheckFailure(null);
  }, [notificationid]);

  const verifyEmailMutation = useMutation({
    onMutate: async (email) => ({
      diagnostic: recordEmailLoginDiagnostic("email_code_request_started", {
        email: normalizeEmailForLogin(email),
      }),
    }),
    mutationFn: async (email) => {
      const normalizedEmail = normalizeEmailForLogin(email);
      const qF = fetchPost("loginemail", {
        email: normalizedEmail,
      });
      const data = await qF();
      console.log("loginemail data", data);
      return data;
    },
    onSuccess: (result, variables, context) => {
      console.log("on verify success", result, variables, context);
      const nextNotificationId = normalizeNotificationId(result?.id);
      recordEmailLoginDiagnostic("email_code_request_success", {
        attemptId: context?.diagnostic?.id,
        email: normalizeEmailForLogin(variables),
        notificationid: nextNotificationId,
      });
      setEmailLoginCheckNotificationId(null);
      setEmailCodeConfirmedAt(null);
      setEmailLoginCheckFailure(null);
      setNotificationid(nextNotificationId);
    },
    onError: (error, variables, context) => {
      console.log("on verify error", error);
      recordEmailLoginDiagnostic("email_code_request_failed", {
        attemptId: context?.diagnostic?.id,
        email: normalizeEmailForLogin(variables),
        error: getAxiosErrorDetails(error),
        reason: classifyEmailLoginError(error),
      });
    },
    onSettled: (data, error, variables, context) => {
      console.log("on verify settled", data, error);
    },
  });

  const confirmloginMutation = useMutation({
    onMutate: async (verificationCode) => ({
      diagnostic: recordEmailLoginDiagnostic("email_code_confirm_started", {
        notificationid,
        codeInput: describeVerificationCodeInput(verificationCode),
      }),
    }),
    mutationFn: async (verificationCode) => {
      const normalizedVerificationCode = normalizeVerificationCode(verificationCode);
      const normalizedNotificationId = normalizeNotificationId(notificationid);
      console.log(
        "confirmlogin",
        "notificationid",
        normalizedNotificationId,
        "codeLength",
        normalizedVerificationCode.length
      );
      const qF = fetchPost("confirmlogin", {
        notificationid: normalizedNotificationId,
        code: normalizedVerificationCode,
      });
      const data = await qF();
      console.log("confirmlogin data", data);
      return data;
    },
    onSuccess: (result, variables, context) => {
      console.log("on confirmlogin success", result, variables, context);
      const confirmedAt = Date.now();
      const checkLoginNotificationId = normalizeVerificationCode(variables);

      // Sonicola names this field "notificationid" in checklogin, but the
      // value it expects after confirmlogin is the email verification code.
      setEmailLoginCheckNotificationId(checkLoginNotificationId);
      setEmailCodeConfirmedAt(confirmedAt);
      setEmailLoginCheckFailure(null);
      recordEmailLoginDiagnostic("email_code_confirm_accepted", {
        attemptId: context?.diagnostic?.id,
        notificationid,
        checkLoginNotificationId,
        codeInput: describeVerificationCodeInput(variables),
        responseStatus: result?.status ?? null,
        confirmedAt: new Date(confirmedAt).toISOString(),
      });
    },
    onError: (error, variables, context) => {
      console.log("on confirmlogin error", error);
      recordEmailLoginDiagnostic("email_code_confirm_failed", {
        attemptId: context?.diagnostic?.id,
        notificationid,
        codeInput: describeVerificationCodeInput(variables),
        error: getAxiosErrorDetails(error),
        reason: classifyEmailLoginError(error),
      });
    },
  });

  const verifyEmailStatusQuery = useQuery(
    ["verifyEmailStatus", emailLoginCheckNotificationId],
    async () => {
      const qF = fetchPost("checklogin", {
        notificationid: emailLoginCheckNotificationId,
      });
      const response = await qF();
      console.log("checklogin data", response);

      if (emailLoginCheckNotificationId && isEmailLoginPendingResponse(response)) {
        const confirmedElapsedMs = emailCodeConfirmedAt
          ? Date.now() - emailCodeConfirmedAt
          : null;

        recordEmailLoginDiagnostic("email_login_check_pending", {
          notificationid,
          checkLoginNotificationId: emailLoginCheckNotificationId,
          responseStatus: response?.status ?? null,
          responseMessage: response?.msg ?? response?.message ?? null,
          confirmedElapsedMs,
        });

        if (
          emailCodeConfirmedAt &&
          confirmedElapsedMs >= EMAIL_LOGIN_CHECK_TIMEOUT_MS
        ) {
          const failure = toEmailLoginCheckFailure({
            response,
            notificationid: emailLoginCheckNotificationId,
          });

          setEmailLoginCheckFailure(failure);
          recordEmailLoginDiagnostic("email_login_check_business_failed", {
            notificationid,
            checkLoginNotificationId: emailLoginCheckNotificationId,
            error: {
              message: failure.message,
              backendStatus: failure.status,
            },
            reason: "login_not_found",
          });
        }
      }

      if (
        emailLoginCheckNotificationId &&
        response?.status === "success" &&
        !!response?.data?.token
        ) {
          recordEmailLoginDiagnostic("email_login_check_success", {
            notificationid,
            checkLoginNotificationId: emailLoginCheckNotificationId,
            userid: response?.data?.userid ?? response?.data?.id ?? null,
            email: response?.data?.email ?? null,
          });
          setNotificationid(null);
          setEmailLoginCheckNotificationId(null);
          setEmailLoginCheckFailure(null);
          const data = response.data;
          rememberInteractiveLogin(data);
          void reportEvent({
            type: EVENT_TYPES.LOGIN,
            user: data,
            payload: {
              method: "email_code",
              notificationid,
            },
            resolutionMode: "login",
            attemptId: `email_code:${notificationid}`,
          });
          if (shouldEmitRegisterEvent(data)) {
            void reportEvent({
              type: EVENT_TYPES.REGISTER,
              user: data,
              payload: {
                ...buildRegisterPayload(data, "email_code"),
                notificationid,
              },
              resolutionMode: "login",
            });
          }
          login(data.token, data);
        }
        return response;
      },
    {
      enabled: !!emailLoginCheckNotificationId && !emailLoginCheckFailure,
      refetchInterval: (data) =>
        data?.status === "success" || emailLoginCheckFailure ? false : 5000,
      onError: (error) => {
        recordEmailLoginDiagnostic("email_login_check_failed", {
          notificationid,
          checkLoginNotificationId: emailLoginCheckNotificationId,
          error: getAxiosErrorDetails(error),
          reason: classifyEmailLoginError(error),
        });
      },
    }
  );

  // useEffect(() => {
  //   if (
  //     notificationid &&
  //     verifyEmailStatusQuery.isSuccess &&
  //     verifyEmailStatusQuery.data?.status === "success"
  //   ) {
  //     setNotificationid(null);
  //     const data = verifyEmailStatusQuery.data.data;
  //     login(data.token, data);
  //   }
  // }, [
  //   verifyEmailStatusQuery.data,
  //   verifyEmailStatusQuery.isSuccess,
  //   setUser,
  //   notificationid,
  //   login,
  // ]);

  return {
    mutation: loginMutation,
    userQuery: userQuery,
    login: loginMutation.mutate,
    logout: logout,
    loginData: loginData,
    user: user,
    setRadioVisited: setRadioVisited,
    getRadioInfo: getRadioInfo,
    token: token,
    logged: token !== null,
    initiated: userInitiated,
    updateUser: updateUser,
    loginRedirect: loginRedirect,
    setLoginRedirect: setLoginRedirect,
    editUser: editMutation.mutate,
    editUserMutation: editMutation,
    uploadAvatar: uploadAvatarMutation.mutate,
    uploadAvatarMutation: uploadAvatarMutation,
    verifyEmail: verifyEmailMutation.mutate,
    verifyEmailMutation: verifyEmailMutation,
    notificationid: notificationid,
    verifyEmailStatusQuery: verifyEmailStatusQuery,
    emailLoginCheckFailure,
    confirmloginMutation,
    setNotificationid,
  };
};

export const useFavorites = () => {
  const [mutatingObserver, setMutatingObserver] = useState(false);
  const { token } = useUser();

  const favoritesQuery = useQuery(
    ["favorites", token],
    fetchPost(
      "favorites",
      {
        token: token,
        action: "list",
      },
      { enabled: token !== null }
    )
  );

  // Define the "addfavorite" mutation
  apiQueryClient.setMutationDefaults("addfavorite", {
    mutationFn: async (dataToSend) => {
      const { data } = await axios.post(`${API_SONICOLA_URL}favorites`, {
        ...dataToSend,
        token: token,
      });
      return data;
    },
    onMutate: async (variables) => { },
    onSuccess: (result, variables, context) => {
      // Replace optimistic todo in the user list with the result
      updateFavorites();
      setMutatingObserver(true);
      return result;
    },
    onError: (error, variables, context) => {
      console.log("on addfavorite error");
      updateFavorites();
      setMutatingObserver(true);
    },
    retry: 1,
  });

  const mutation = useMutation("addfavorite");

  const updateFavorites = () => {
    apiQueryClient.invalidateQueries("favorites");
  };

  if (!favoritesQuery.isFetching && mutatingObserver) {
    setMutatingObserver(false);
  }

  return {
    ...favoritesQuery,
    mutation: mutation,
    isMutating: mutation.isLoading || mutatingObserver,
    addFavorite: (songid) => {
      mutation.mutateAsync({ action: "add", songid: songid });
    },
    removeFavorite: (songid) => {
      mutation.mutateAsync({ action: "remove", songid: songid });
    },
    updateFavorites: updateFavorites,
  };
};

/**
 * Creates a react-query query for calling "songlist"
 * @param {string} playlistid
 * @param {string} search
 * @param {number} offset
 * @returns {object}
 */
export const useSongs = (playlistid, search, radioid) => {
  const { token } = useUser();

  const SONGS_PER_PAGE = 20;

  const dataToSend = {
    token: token,
    offset: 0,
    limit: search ? 0 : SONGS_PER_PAGE,
    radioid: radioid,
  };

  if (search !== "") {
    dataToSend.filter = search;
  } else if (playlistid !== "") {
    dataToSend.playlistid = playlistid;
  }

  const songsQuery = useInfiniteQuery(
    ["songlist", token, dataToSend.radioid, playlistid, search],
    fetchPostPage("songlist", dataToSend, { enabled: token !== null }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (search !== "") return undefined;
        if (!lastPage?.data?.length) return undefined;

        // Replay the adaptive algorithm across all prior pages to derive
        // current {offset, limit} state. A "clean" page (all returned
        // songs were new) means the API is healthy — advance offset and
        // reset limit to SONGS_PER_PAGE. A "dirty" page (some duplicates
        // dropped by dedup) means the API is duplicating — keep offset and
        // grow limit by SONGS_PER_PAGE to widen the fetch window gradually.
        // Stop when deduped unique count reaches the API-reported total.
        let offset = 0;
        let limit = SONGS_PER_PAGE;
        const seenIds = new Set();

        for (let i = 0; i < pages.length; i++) {
          const pageData = pages[i].data ?? [];
          const newCount = pageData.filter((s) => !seenIds.has(s.songid)).length;
          pageData.forEach((s) => seenIds.add(s.songid));
          const cleanPage = newCount === pageData.length;

          if (i === pages.length - 1) {
            if (newCount === 0) return undefined;
            if (cleanPage) {
              offset += pageData.length;
              limit = SONGS_PER_PAGE;
            } else {
              limit += SONGS_PER_PAGE;
            }
          } else {
            if (cleanPage) {
              offset += pageData.length;
              limit = SONGS_PER_PAGE;
            } else {
              limit += SONGS_PER_PAGE;
            }
          }
        }

        const pageParams = {
          token: token,
          offset,
          limit,
          radioid: dataToSend.radioid,
        };
        if (playlistid !== "") {
          pageParams.playlistid = playlistid;
        }
        return pageParams;
      },
      enabled:
        (typeof dataToSend.radioid === "string" ||
          typeof dataToSend.radioid === "number") &&
        (playlistid !== "" || search !== ""),
      keepPreviousData: true,
      staleTime: 1000,
      getPreviousPageParam: (firstPage, pages) => {
        if (pages.length === 0) return undefined;
        return (pages.length - 1) * SONGS_PER_PAGE;
      },
    }
  );

  const songs = songsQuery.data?.pages
    ?.map((page) => page.data)
    .flat()
    .filter(
      (value, index, self) =>
        index === self.findIndex((t) => t.songid === value.songid)
    );

  return {
    songs: songs,
    total: songsQuery?.data?.pages[0].total,
    loadMore: songsQuery.fetchNextPage,
    ...songsQuery,
  };
};

export const useSingleSong = (radioid, songname, songid) => {
  const { user } = useUser();
  const { token = null } = user ?? {};

  const parts =
    typeof songname === "string" && songname !== ""
      ? songname.split(" - ")
      : [songname];

  let filter = songname;

  if (parts.length > 0) {
    parts.shift();
    filter = parts.join(" ");
  }
  filter = filter.slice(0, 8);

  const dataToSend = {
    token,
    limit: 1000,
    offset: 0,
    radioid,
    filter,
  };

  const songQuery = useQuery(
    ["singlesong", radioid, songname, songid],
    fetchPost("songlist", dataToSend)
  );

  return {
    ...songQuery,
    song: songQuery.data?.find((song) => song.songid === songid),
  };
};

export const usePlaylist = (
  radioid,
  playlistid,
  random = true,
  offset = 0,
  limit = 30
) => {
  const { user } = useUser();
  const { token = null } = user ?? {};
  const playlistQuery = useQuery(
    ["playlist", playlistid, radioid, offset, limit],
    fetchPost("songlist", {
      token,
      limit,
      offset,
      radioid,
      playlistid,
    })
  );

  const songs = useMemo(() => {
    const data = playlistQuery?.data?.data
      ? playlistQuery?.data?.data
      : playlistQuery?.data
        ? playlistQuery?.data
        : [];
    // console.log("playlistQuery data", playlistQuery?.data);
    // console.log("songs data", data);
    if (random) {
      return data.sort(() => Math.random() - 0.5);
    }
    return data;
  }, [playlistQuery.data, random]);

  return { ...playlistQuery, songs };
};

export const useVote = (radioid) => {
  const { user } = useUser();
  const { token = null } = user ?? {};

  // Define the "vote" mutation
  apiQueryClient.setMutationDefaults("vote", {
    retries: 1,
    mutationFn: async (params) => {
      if (params.scid !== undefined) {
        const dataToSend = {
          token: token,
          votes: params.votes,
          scid: params.scid,
          radioid: radioid,
        };
        const response = await axios.post(
          `${API_SONICOLA_URL}votesong`,
          dataToSend
        );
        console.log(
          "votesong response.data",
          typeof response.data,
          response?.data
        );
        if (typeof response.data === "object") {
          return response.data;
        } else {
          return null;
        }
      } else {
        const dataToSend = {
          token: token,
          radioid: radioid,
          votes: params.votes,
          songid: params.songid,
        };
        const response = await axios.post(
          `${API_SONICOLA_URL}requestsong`,
          dataToSend
        );
        console.log(
          "requestsong response.data",
          typeof response.data,
          response.data
        );
        if (typeof response.data !== "undefined") {
          return response.data;
        } else {
          console.log("requestsong response", response);
          return response;
        }
      }
    },
    onMutate: async (variables) => ({
      eventAttemptId: variables?.eventAttemptId ?? createEventAttemptId(),
    }),
    onSuccess: (result, variables, context) => {
      // Replace optimistic todo in the user list with the result
      apiQueryClient.setQueryData("vote", (old) => result);
      apiQueryClient.invalidateQueries(["singlecola"]);
      apiQueryClient.invalidateQueries(["nextsong"]);
      apiQueryClient.invalidateQueries(["user"]);

      if (isBusinessSuccess(result)) {
        const isVote = typeof variables?.scid !== "undefined";
        void reportEvent({
          type: isVote ? EVENT_TYPES.VOTE : EVENT_TYPES.REQUEST,
          user,
          radio: {
            radioid,
            nameseo: variables?.nameseo,
          },
          payload: {
            songid: variables?.songid ?? null,
            scid: variables?.scid ?? null,
            votes: variables?.votes ?? null,
            songname: variables?.songname ?? null,
            artist: variables?.artist ?? null,
            result,
          },
          attemptId: getEventAttemptId(variables, context),
        });
        emitMarketingEvent(
          isVote
            ? MARKETING_UI_EVENTS.VOTE_SUCCESS
            : MARKETING_UI_EVENTS.REQUEST_SUCCESS,
          {
            radioid,
            nameseo: variables?.nameseo,
            songid: variables?.songid ?? null,
            scid: variables?.scid ?? null,
            votes: variables?.votes ?? null,
            songname: variables?.songname ?? null,
            artist: variables?.artist ?? null,
            result,
          }
        );
      }

      return result;
    },
    onError: (error, variables, context) => {
      console.log("on vote error", error, error.response, variables, context);
      // Remove optimistic user from the user list
      apiQueryClient.setQueryData("vote", (old) => ({
        token: null,
        name: "",
      }));
    },
    retry: 1,
  });

  const mutation = useMutation("vote");

  const vote = (params) => {
    mutation.mutateAsync(params);
  };

  const voteData = mutation.data;

  return {
    mutation: mutation,
    vote: vote,
    voteData: voteData,
  };
};

export const useCode = () => {
  const { user, logged, token: sessionToken } = useUser();
  // Use the authoritative session token from context, not user?.token. React 17
  // does not batch the setToken/setUser updates in the async login onSuccess
  // callback, so there is an intermediate render where `logged` is already true
  // but `user` still holds the previous (logged-out) value. Reading user?.token
  // there yields undefined, and the post-login redeem then fires with no token,
  // which the backend rejects with a 400. The context token is set together
  // with `logged`, so it is reliable in that window.
  const token = logged ? (sessionToken ?? user?.token ?? null) : null;

  // Define the "colaCode" mutation
  apiQueryClient.setMutationDefaults("colaCode", {
    mutationFn: async (params) => {
      const dataToSend = {
        token: token,
        ccode: params.ccode,
      };
      const response = await axios.post(
        `${API_SONICOLA_URL}validateColaCode`,
        dataToSend
      );
      return response.data;
    },
    onMutate: async (variables) => { },
    onSuccess: (result, variables, context) => {
      // Replace optimistic todo in the user list with the result
      apiQueryClient.setQueryData("colaCode", (old) => result);
      return result;
    },
    onError: (error, variables, context) => {
      console.log(
        "on colaCode error",
        error,
        error.response,
        variables,
        context
      );
      // Remove optimistic user from the user list
      apiQueryClient.setQueryData("colaCode", (old) => ({
        token: null,
        name: "",
      }));
    },
    // Redeem codes are single-use on the backend; an automatic retry can consume
    // a successful first request and surface the retry as "already used".
    retry: false,
  });

  const mutation = useMutation("colaCode");

  const sendCode = useCallback(
    async (
      params,
      successCallback = () => { },
      errorCallback = () => { },
      doneCallback = () => { }
    ) => {
      const redeemSubmission = acquireRedeemSubmission({
        encodedCode: params?.encodedCode,
        code: params?.ccode ?? null,
      });

      if (!redeemSubmission.acquired) {
        if (redeemSubmission.entry?.status === "success") {
          successCallback(
            redeemSubmission.entry.result ?? { status: "success" }
          );
        }
        doneCallback();
        return;
      }

      try {
        const codeResponse = await mutation.mutateAsync(params);
        if (codeResponse !== undefined) {
          markRedeemSubmissionSuccess(params?.encodedCode, codeResponse);
          void reportRedeemSuccess({
            result: codeResponse,
            user,
            radio: params?.radio,
            source: params?.source,
            code: params?.ccode ?? null,
            encodedCode: params?.encodedCode ?? null,
          });
          successCallback(codeResponse);
        } else {
          releaseRedeemSubmission(params?.encodedCode);
        }
      } catch (error) {
        releaseRedeemSubmission(params?.encodedCode);
        console.error("sendcode error", error);
        errorCallback(error);
      } finally {
        doneCallback();
      }
    },
    [mutation, user]
  );

  const codeData = mutation.data;

  return {
    mutation: mutation,
    sendCode: sendCode,
    codeData: codeData,
  };
};

export const useCards = () => {
  const { user } = useUser();
  const { token = null } = user ?? {};

  const cardsQuery = useQuery(
    ["cards", token],
    fetchPost("cards", { token: token }),
    { enabled: token !== null }
  );

  const updateCards = () => {
    apiQueryClient.invalidateQueries("cards");
  };

  // Define the "addCard" mutation
  apiQueryClient.setMutationDefaults("addCard", {
    mutationFn: async (params) => {
      const dataToSend = {
        token: token,
        name: params.name,
        card_num: params.card_num,
        exp_month: params.exp_month,
        exp_year: params.exp_year,
        stripeToken: params.stripeToken,
        cvc: params.cvc,
      };
      const response = await axios.post(
        `${API_SONICOLA_URL}addCard`,
        dataToSend
      );
      return response.data;
    },
    onMutate: async (variables) => { },
    onSuccess: (result, variables, context) => {
      console.log("on addCard success", result);
      // Replace optimistic todo in the user list with the result
      apiQueryClient.setQueryData("addCard", (old) => result);
      apiQueryClient.invalidateQueries("cards");
      return result;
    },
    onError: (error, variables, context) => {
      console.log(
        "on addCard error",
        error,
        error.response,
        variables,
        context
      );
      // Remove optimistic user from the user list
      apiQueryClient.setQueryData("addCard", (old) => ({
        token: null,
        name: "",
      }));
    },
    retry: 1,
  });

  const addCardMutation = useMutation("addCard");

  const addCard = async (params, callback = () => { }) => {
    console.log("useApi addCard ", params);
    try {
      const codeResponse = await addCardMutation.mutateAsync(params);
      if (codeResponse !== undefined) {
        callback();
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const addCardData = addCardMutation.data;

  // Define the "deleteCard" mutation
  apiQueryClient.setMutationDefaults("deleteCard", {
    mutationFn: async (params) => {
      const dataToSend = {
        token: token,
        ucid: params.ucid,
      };
      const response = await axios.post(
        `${API_SONICOLA_URL}deletecard`,
        dataToSend
      );
      return response.data;
    },
    onMutate: async (variables) => { },
    onSuccess: (result, variables, context) => {
      // Replace optimistic todo in the user list with the result
      apiQueryClient.setQueryData("deleteCard", (old) => result);
      apiQueryClient.invalidateQueries("cards");
      return result;
    },
    onError: (error, variables, context) => {
      console.log(
        "on deleteCard error",
        error,
        error.response,
        variables,
        context
      );
      // Remove optimistic user from the user list
      apiQueryClient.setQueryData("deleteCard", (old) => ({
        token: null,
        name: "",
      }));
    },
    retry: 1,
  });

  const deleteCardMutation = useMutation("deleteCard");

  const deleteCard = async (params, callback = () => { }) => {
    try {
      const codeResponse = await deleteCardMutation.mutateAsync(params);
      if (codeResponse !== undefined) {
        callback();
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const deleteCardData = deleteCardMutation.data;

  return {
    cardsQuery: cardsQuery,
    cards: cardsQuery.data?.cards,
    isLoading: cardsQuery.isLoading,
    isError: cardsQuery.isError,
    updateCards: updateCards,
    error: cardsQuery.error,
    addCardMutation: addCardMutation,
    addCard: addCard,
    addCardData: addCardData,
    deleteCardMutation: deleteCardMutation,
    deleteCard: deleteCard,
    deleteCardData: deleteCardData,
  };
};

export const ApiProvider = ({ children }) => {
  const [currentRadioid, setCurrentRadioid] = useState(null);
  const [user, setUser] = useState(null);
  const [initiated, setInitiated] = useState(false);
  const [token, setToken] = useState(null);
  const isBrowser = useCallback(() => typeof window !== "undefined", []);

  const [loginRedirect, setLoginRedirect] = useState(() =>
    getLoginResumePath("/sonicboxes")
  );

  useEffect(() => {
    initializeEventEmitter();
  }, []);

  useEffect(() => {
    const savedLogin = getLegacyRedirectLogin();
    if (typeof savedLogin === "string" && savedLogin !== "") {
      setLoginRedirect(savedLogin);
    }
  }, []);

  const saveUser = useCallback((user) => {
    window.localStorage.setItem("sonicboxUser", JSON.stringify(user));
  }, []);

  const getUser = useCallback(() => {
    if (isBrowser() && window.localStorage.getItem("sonicboxUser")) {
      const storedUser = JSON.parse(
        window.localStorage.getItem("sonicboxUser")
      );
      // console.log("storedUser", storedUser)
      if (storedUser.hasOwnProperty("token") && storedUser.token !== null) {
        return storedUser;
      }
    }
    return { token: null, name: "" };
  }, [isBrowser]);

  useEffect(() => {
    if (user === null) {
      const storedUser = getUser();
      if (storedUser.hasOwnProperty("token") && storedUser.token !== null) {
        setUser(storedUser);
        setToken(storedUser.token);
      }
    } else {
      saveUser(user);
    }
    setInitiated(true);
  }, [user, getUser, saveUser]);

  useEffect(() => {
    if (user?.userid && user?.email) {
      void flushQueuedEvents({ user });
    }
  }, [user?.userid, user?.email, user]);

  const login = useCallback((newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
  }, []);

  const [ticker, setTicker] = useState(null);
  useEffect(() => {
    if (ticker === null) {
      setInterval(() => {
        setTicker(new Date().getTime());
      }, 5000);
    }
  }, [ticker]);

  useEffect(() => {
    if (ticker !== null && user === null) {
      const storedUser = getUser();
      if (storedUser.hasOwnProperty("token") && storedUser.token !== null) {
        login(storedUser.token, storedUser);
      }
    }
  }, [user, getUser, ticker, login]);

  const logout = useCallback(
    (callback) => {
      apiQueryClient.removeQueries(["user", token]);
      console.log("logout");
      console.log(window.FB);
      if (window?.FB) {
        // revoke app permissions to logout completely because FB.logout() doesn't remove FB cookie
        window.FB.api("/me/permissions", "delete", null, (...args) => {
          console.log("FB delete permissions callback", args);
          window.FB.logout();
        });
      }
      const radioEntryPath = restartRadioEntry();
      if (!radioEntryPath) clearLegacyRedirectLogin();
      setLoginRedirect(radioEntryPath || "/sonicboxes");
      setUser({ token: null, name: "" });
      saveUser("");
      setToken(null);
      setCurrentRadioid(null);
      navigate(radioEntryPath || "/");
      if (typeof callback === "function") callback();
    },
    [token, setUser, setToken, saveUser]
  );

  useEffect(() => {
    globalHistory.listen(({ pathname }) => {
      console.log("pathname", pathname);
    });
  }, []);

  return (
    <Location>
      {({ location }) => {
        const matchedRadio = location.pathname.match(/\/sonicbox\/(.+)/);
        const radioNameseo =
          matchedRadio !== null ? matchedRadio[1] : null;
        const matchedTaskNameseo = location.pathname.match(/\/task\/(.+)/);
        const taskNameseo =
          matchedTaskNameseo !== null ? matchedTaskNameseo[1] : null;
        const isTask = taskNameseo !== null;

        return (
          <RadioContext.Provider
            value={{
              nameseo: isTask ? taskNameseo : radioNameseo,
              isTask: isTask,
              currentRadioid: currentRadioid,
              setCurrentRadioid: setCurrentRadioid,
            }}
          >
            <UserContext.Provider
              value={{
                user: user,
                setUser: setUser,
                token: token,
                userInitiated: initiated,
                setToken: setToken,
                login: login,
                logout: logout,
                loginRedirect: loginRedirect,
                setLoginRedirect: setLoginRedirect,
              }}
            >
              <QueryClientProvider client={apiQueryClient}>
                <StoredCodesProvider>{children}</StoredCodesProvider>
              </QueryClientProvider>
            </UserContext.Provider>
          </RadioContext.Provider>
        );
      }}
    </Location>
  );
};

const StoredCodesProvider = ({ children }) => {
  const { logged, updateUser } = useUser();
  const { sendCode } = useCode();
  const [sentCode, setSentCode] = useState(null);

  const removeStoredCode = useCallback(
    (storedCode) => {
      removeStoredRedeemCode(storedCode);
      setSentCode(null);
    },
    []
  );

  useEffect(() => {
    const activeEntryIntent = getEntryIntent();
    if (activeEntryIntent?.redeem?.encodedCode) {
      return;
    }

    const storedCodes = getStoredRedeemCodes();
    if (logged && storedCodes.length > 0) {
      const firstCode = storedCodes[0];
      const encodedCode = firstCode?.encodedCode;
      const splitted = decryptCode(encodedCode).split("-");
      if (splitted.length > 0) {
        const codeToSend = splitted[0];
        if (codeToSend.length > 0 && sentCode !== codeToSend) {
          setSentCode(codeToSend);
          sendCode(
            {
              ccode: codeToSend,
              encodedCode: firstCode?.encodedCode ?? null,
              source: firstCode?.source === "legacy" ? "qr" : firstCode?.source,
              radio: firstCode?.radioHint ?? null,
            },
            (response) => {
              updateUser();
              console.log("response", response);
              if (
                response.hasOwnProperty("status") &&
                response.status === "success"
              ) {
                removeStoredCode(encodedCode);
                setSentCode(null);
              }
            },
            (error) => {
              console.log("sendcode error", error);
              if (error.hasOwnProperty("response")) {
                console.log("error response", error.response);
                if (error.response?.data?.msg === "Code does not work") {
                  removeStoredCode(encodedCode);
                  setSentCode(null);
                  alert("El código ya no es válido");
                }
              }
            }
          );
        }
      }
    }
  }, [logged, sendCode, sentCode, removeStoredCode, updateUser]);
  return children;
};

export const getNameseoByRadioid = async (radioid) => {
  const resolvedRadioid = resolveRedirectedRadioid(radioid);
  const response = await axios.post(`${API_SONICOLA_URL}singlecola`, {
    radioid: resolvedRadioid,
  });
  console.log("response", response);
  if (response?.data?.data?.data?.nameseo) {
    return response.data.data.data.nameseo;
  }
  if (response?.data?.data?.nameseo) {
    return response.data.data.nameseo;
  }
  if (response?.data?.nameseo) {
    return response.data.nameseo;
  }
  return null;
};

export const useContact = () => {
  const mutation = useMutation(
    (data) => axios.post(`${API_SONICOLA_URL}sendMessage`, data),
    {
      onSuccess: (data) => {
        console.log("data", data);
      },
    }
  );
  return mutation;
};
